/**
 * 剧本相关 API Hooks
 */
import { useEffect, useCallback } from 'react';
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { screenplayApi } from '@/services/api/screenplay';
import { isNonRetryableHttpError } from '@/lib/error-utils';
import { nextParseStatusFailureCount } from '@/lib/screenplay-upload-state';
import { useOrgContextStore } from '@/stores/orgContextStore';
import {
  invalidateProjectLists,
  invalidateProjectSubprojects,
  invalidateProjectDetail,
  setProjectListsData,
  setProjectDetailData,
} from '@/hooks/api/useProjects';
import { invalidateFolderTrees, setFolderTreesData } from '@/hooks/api/useFolders';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { storyboardReviewKeys } from '@/hooks/api/useStoryboardReviews';
import type {
  Screenplay,
  ScreenplayListParams,
  CreateScreenplayDto,
  ImportStoryboardDto,
  UpdateScreenplayDto,
  UploadScreenplayDto,
  ParseStatusResponse,
  ScreenplayScene,
} from '@/types/screenplay';

// Query Keys
export const screenplayKeys = {
  all: ['screenplays'] as const,
  lists: () => [...screenplayKeys.all, 'list'] as const,
  listRoot: (projectId: string) => [...screenplayKeys.lists(), projectId] as const,
  list: (
    activeOrgId: string | null,
    projectId: string,
    params?: Omit<ScreenplayListParams, 'projectId'>
  ) =>
    [
      ...screenplayKeys.listRoot(projectId),
      activeOrgId,
      {
        status: params?.status ?? null,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
      },
    ] as const,
  details: () => [...screenplayKeys.all, 'detail'] as const,
  detail: (id: string) => [...screenplayKeys.details(), id] as const,
  parseStatus: (id: string) => [...screenplayKeys.all, 'parse-status', id] as const,
  scenes: (id: string) => [...screenplayKeys.all, 'scenes', id] as const,
  dialogDirectoryRoot: () => [...screenplayKeys.all, 'dialog-directory'] as const,
  dialogDirectory: (projectId: string, activeOrgId?: string | null) =>
    [...screenplayKeys.dialogDirectoryRoot(), projectId, activeOrgId ?? null] as const,
};

function getParsedFileUrl(data?: ParseStatusResponse): string | undefined {
  return data?.fileUrl || data?.fireUrl;
}

interface TreeLikeNode {
  id?: string;
  name?: string;
  children?: TreeLikeNode[];
}

interface ScreenplayListLike<TItem = Record<string, unknown>> {
  items: TItem[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export function shouldSyncProjectNameAfterScreenplayUpdate(data: UpdateScreenplayDto): boolean {
  return typeof data.name === 'string' && data.name.trim().length > 0;
}

function removeScreenplayFromListCache<TItem extends { screenplayId?: string }>(
  oldData: unknown,
  screenplayId: string
): unknown {
  if (
    !oldData ||
    typeof oldData !== 'object' ||
    !('items' in oldData) ||
    !Array.isArray((oldData as { items?: unknown[] }).items)
  ) {
    return oldData;
  }

  const typed = oldData as ScreenplayListLike<TItem>;
  const nextItems = typed.items.filter((item) => item?.screenplayId !== screenplayId);

  if (nextItems.length === typed.items.length) {
    return oldData;
  }

  return {
    ...typed,
    items: nextItems,
    total: typeof typed.total === 'number' ? Math.max(0, typed.total - 1) : typed.total,
    pageSize:
      typeof typed.pageSize === 'number'
        ? Math.min(typed.pageSize, nextItems.length || typed.pageSize)
        : typed.pageSize,
    totalPages:
      typeof typed.totalPages === 'number' ? Math.max(1, typed.totalPages) : typed.totalPages,
  };
}

function patchTreeNodeName(
  nodes: TreeLikeNode[] | undefined,
  targetId: string,
  newName: string
): { nextNodes: TreeLikeNode[] | undefined; changed: boolean } {
  if (!nodes || nodes.length === 0) {
    return { nextNodes: nodes, changed: false };
  }

  let changed = false;
  const nextNodes = nodes.map((node) => {
    let nodeChanged = false;
    let nextNode: TreeLikeNode = node;

    if (node.id === targetId && node.name !== newName) {
      nextNode = { ...nextNode, name: newName };
      nodeChanged = true;
    }

    const childPatched = patchTreeNodeName(node.children, targetId, newName);
    if (childPatched.changed) {
      nextNode = { ...nextNode, children: childPatched.nextNodes };
      nodeChanged = true;
    }

    if (nodeChanged) {
      changed = true;
    }
    return nodeChanged ? nextNode : node;
  });

  return { nextNodes: changed ? nextNodes : nodes, changed };
}

export function invalidateScreenplayCreationCaches(
  queryClient: QueryClient,
  {
    requestedProjectId,
    createdProjectId,
  }: {
    requestedProjectId: string;
    createdProjectId?: string | null;
  }
): void {
  void queryClient.invalidateQueries({
    queryKey: screenplayKeys.listRoot(requestedProjectId),
  });
  invalidateProjectSubprojects(queryClient, requestedProjectId);
  if (createdProjectId && createdProjectId !== requestedProjectId) {
    void queryClient.invalidateQueries({
      queryKey: screenplayKeys.listRoot(createdProjectId),
    });
  }
  void queryClient.invalidateQueries({ queryKey: screenplayKeys.dialogDirectoryRoot() });
}

/**
 * 获取项目的所有剧本
 */
export function useScreenplays(
  projectId: string | null,
  params?: Omit<ScreenplayListParams, 'projectId'> & { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const { enabled = true, ...queryParams } = params ?? {};
  return useQuery({
    queryKey: screenplayKeys.list(activeOrgId, projectId!, queryParams),
    queryFn: () => screenplayApi.getAll({ projectId: projectId!, ...queryParams }),
    enabled: !!projectId && enabled,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取单个剧本
 */
export function useScreenplay(
  id: string | null,
  options?: {
    refetchOnMount?: boolean | 'always';
  }
) {
  return useQuery({
    queryKey: screenplayKeys.detail(id!),
    queryFn: () => screenplayApi.getById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
    refetchOnMount: options?.refetchOnMount,
  });
}

export function useScreenplaysByIds(screenplayIds: string[]) {
  return useQueries({
    queries: screenplayIds.map((screenplayId) => ({
      queryKey: screenplayKeys.detail(screenplayId),
      queryFn: () => screenplayApi.getById(screenplayId),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      enabled: Boolean(screenplayId),
    })),
  });
}

export function useScreenplayScenes(screenplayId: string | null) {
  return useQuery<ScreenplayScene[]>({
    queryKey: screenplayKeys.scenes(screenplayId!),
    queryFn: () => screenplayApi.getScenes(screenplayId!),
    enabled: !!screenplayId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * 创建文本剧本
 */
export function useCreateScreenplay() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (data: CreateScreenplayDto) => screenplayApi.create(data, activeOrgId),
    onSuccess: (newScreenplay, variables) => {
      // 使列表缓存失效
      invalidateScreenplayCreationCaches(queryClient, {
        requestedProjectId: variables.projectId,
        createdProjectId: newScreenplay.projectId,
      });
      invalidateProjectLists(queryClient);
      invalidateProjectDetail(queryClient, activeOrgId, variables.projectId);
      if (newScreenplay.projectId && newScreenplay.projectId !== variables.projectId) {
        invalidateProjectDetail(queryClient, activeOrgId, newScreenplay.projectId);
      }
      invalidateFolderTrees(queryClient);
    },
  });
}

/**
 * 更新剧本
 */
export function useUpdateScreenplay() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScreenplayDto }) =>
      screenplayApi.update(id, data),
    onSuccess: (updatedScreenplay, variables) => {
      if (updatedScreenplay) {
        const screenplayId = updatedScreenplay.screenplayId;
        const projectId = updatedScreenplay.projectId;
        const newName = updatedScreenplay.name;
        const shouldSyncProjectName = shouldSyncProjectNameAfterScreenplayUpdate(variables.data);

        // 更新单个剧本缓存
        queryClient.setQueryData<Screenplay>(
          screenplayKeys.detail(screenplayId),
          updatedScreenplay
        );
        // 立即 patch 所有剧本列表缓存（父项目/子项目维度都覆盖），避免关闭重开弹窗读到旧名称
        queryClient.setQueriesData({ queryKey: screenplayKeys.lists() }, (oldData: unknown) => {
          if (
            !oldData ||
            typeof oldData !== 'object' ||
            !('items' in oldData) ||
            !Array.isArray((oldData as { items?: unknown[] }).items)
          ) {
            return oldData;
          }

          const typed = oldData as { items: Array<Record<string, unknown>> };
          let changed = false;
          const nextItems = typed.items.map((item) => {
            if (item?.screenplayId !== screenplayId) return item;
            const nextItem = {
              ...item,
              name: updatedScreenplay.name,
              updatedAt: updatedScreenplay.updatedAt,
            };
            if (item?.name === nextItem.name && item?.updatedAt === nextItem.updatedAt) {
              return item;
            }
            changed = true;
            return nextItem;
          });
          return changed ? { ...typed, items: nextItems } : oldData;
        });

        // 立即 patch 弹窗剧本目录缓存（ParseFlowDialogContainer 使用的独立 key）
        queryClient.setQueriesData(
          { queryKey: screenplayKeys.dialogDirectoryRoot() },
          (oldData: unknown) => {
            if (
              !oldData ||
              typeof oldData !== 'object' ||
              !('items' in oldData) ||
              !Array.isArray((oldData as { items?: unknown[] }).items)
            ) {
              return oldData;
            }

            const typed = oldData as { items: Array<Record<string, unknown>> };
            let changed = false;
            const nextItems = typed.items.map((item) => {
              if (item?.screenplayId !== screenplayId) return item;
              const nextItem = {
                ...item,
                name: updatedScreenplay.name,
                updatedAt: updatedScreenplay.updatedAt,
              };
              if (item?.name === nextItem.name && item?.updatedAt === nextItem.updatedAt) {
                return item;
              }
              changed = true;
              return nextItem;
            });
            return changed ? { ...typed, items: nextItems } : oldData;
          }
        );

        if (shouldSyncProjectName) {
          // 仅显式重命名剧本时同步项目名；内容保存不能污染单集项目名称。
          setProjectDetailData(queryClient, activeOrgId, projectId, (oldProject: unknown) => {
            if (!oldProject || typeof oldProject !== 'object') return oldProject;
            if ((oldProject as { name?: string }).name === newName) return oldProject;
            return {
              ...(oldProject as Record<string, unknown>),
              name: newName,
            };
          });

          // 立即 patch 项目列表缓存（若有页面使用 useProjects）
          setProjectListsData(queryClient, (oldData: unknown) => {
            if (
              !oldData ||
              typeof oldData !== 'object' ||
              !('items' in oldData) ||
              !Array.isArray((oldData as { items?: unknown[] }).items)
            ) {
              return oldData;
            }

            const typed = oldData as { items: Array<Record<string, unknown>> };
            let changed = false;
            const nextItems = typed.items.map((item) => {
              if (item?.id !== projectId) return item;
              if (item?.name === newName) return item;
              changed = true;
              return { ...item, name: newName };
            });
            return changed ? { ...typed, items: nextItems } : oldData;
          });

          // 立即 patch 文件夹树缓存中的项目/子项目节点名称（Workspace/ProjectPanel）
          setFolderTreesData(queryClient, (oldData: unknown) => {
            if (
              !oldData ||
              typeof oldData !== 'object' ||
              !('tree' in oldData) ||
              !Array.isArray((oldData as { tree?: unknown[] }).tree)
            ) {
              return oldData;
            }

            const typed = oldData as { tree: TreeLikeNode[] };
            const patched = patchTreeNodeName(typed.tree, projectId, newName);
            if (!patched.changed) return oldData;
            return { ...typed, tree: patched.nextNodes ?? typed.tree };
          });
        }

        // 后台重拉兜底：统一失效所有剧本列表缓存，确保再次打开弹窗时为最新名称
        queryClient.invalidateQueries({ queryKey: screenplayKeys.lists() });
        queryClient.invalidateQueries({ queryKey: screenplayKeys.dialogDirectoryRoot() });
        if (variables.data.content !== undefined) {
          queryClient.invalidateQueries({ queryKey: storyboardReviewKeys.status(projectId) });
        }
        if (shouldSyncProjectName) {
          // 后台重拉兜底，确保最终一致性
          invalidateProjectDetail(queryClient, activeOrgId, projectId);
          invalidateProjectLists(queryClient);
          invalidateFolderTrees(queryClient);
        }
      }
    },
  });
}

/**
 * 删除剧本（软删除，并同步删除关联子项目至回收站）
 */
export function useDeleteScreenplay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => screenplayApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.setQueriesData({ queryKey: screenplayKeys.lists() }, (oldData: unknown) =>
        removeScreenplayFromListCache(oldData, id)
      );
      queryClient.setQueriesData(
        { queryKey: screenplayKeys.dialogDirectoryRoot() },
        (oldData: unknown) => removeScreenplayFromListCache(oldData, id)
      );
      // 失效剧本列表与该剧本详情缓存
      queryClient.invalidateQueries({ queryKey: screenplayKeys.lists() });
      queryClient.invalidateQueries({ queryKey: screenplayKeys.dialogDirectoryRoot() });
      queryClient.removeQueries({ queryKey: screenplayKeys.detail(id) });
      queryClient.removeQueries({ queryKey: screenplayKeys.parseStatus(id) });
      queryClient.removeQueries({ queryKey: screenplayKeys.scenes(id) });
      // 子项目已移至回收站，需刷新项目列表与文件夹树
      invalidateProjectLists(queryClient);
      invalidateFolderTrees(queryClient);
      queryClient.invalidateQueries({ queryKey: orgProjectBudgetTreeKeys.all });
    },
  });
}

/**
 * 上传并解析剧本文件
 */
export function useUploadScreenplay() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      data,
      onProgress,
    }: {
      data: UploadScreenplayDto;
      onProgress?: (progress: number) => void;
    }) => screenplayApi.uploadAndParse(data, onProgress, activeOrgId),
    onSuccess: (result, variables) => {
      // 使列表缓存失效
      queryClient.invalidateQueries({
        queryKey: screenplayKeys.lists(),
      });
      queryClient.invalidateQueries({ queryKey: screenplayKeys.dialogDirectoryRoot() });

      const parentId = variables.data.projectId;
      const subId = result.subproject?.projectId;
      invalidateScreenplayCreationCaches(queryClient, {
        requestedProjectId: parentId,
        createdProjectId: subId ?? result.projectId,
      });
      invalidateProjectDetail(queryClient, activeOrgId, parentId);
      if (subId) {
        invalidateProjectDetail(queryClient, activeOrgId, subId);
      }
      invalidateProjectLists(queryClient);
      invalidateFolderTrees(queryClient);
      queryClient.invalidateQueries({ queryKey: orgProjectBudgetTreeKeys.all });

      // 如果是同步解析完成，直接设置详情缓存
      if (result.parsingStatus === 'completed' && result.content) {
        queryClient.setQueryData(screenplayKeys.detail(result.screenplayId), {
          screenplayId: result.screenplayId,
          name: result.name,
          type: result.type,
          content: result.content,
          wordCount: result.wordCount,
          parsingStatus: result.parsingStatus,
          parsedAt: result.parsedAt,
        });
      }
    },
  });
}

/**
 * 导入分镜表格，由后端解析并创建子项目/剧本/分镜
 */
export function useImportStoryboard() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      data,
      onProgress,
    }: {
      data: ImportStoryboardDto;
      onProgress?: (progress: number) => void;
    }) => screenplayApi.importStoryboard(data, onProgress, activeOrgId),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: screenplayKeys.lists(),
      });
      invalidateProjectLists(queryClient);
      invalidateFolderTrees(queryClient);
      queryClient.invalidateQueries({ queryKey: screenplayKeys.dialogDirectoryRoot() });
      const requestedProjectId = variables.data.projectId ?? variables.data.parentProjectId;
      const subId = result.subprojectId;
      if (requestedProjectId) {
        invalidateScreenplayCreationCaches(queryClient, {
          requestedProjectId,
          createdProjectId: subId ?? result.projectId,
        });
        invalidateProjectDetail(queryClient, activeOrgId, requestedProjectId);
      }
      if (subId) {
        invalidateProjectDetail(queryClient, activeOrgId, subId);
      }
      queryClient.invalidateQueries({ queryKey: orgProjectBudgetTreeKeys.all });
    },
  });
}

/**
 * 查询解析状态（支持轮询）
 *
 * @param id 剧本 ID
 * @param enabled 是否启用查询
 * @param refetchInterval 轮询间隔（毫秒），默认 2000ms
 */
export const SCREENPLAY_PARSE_STATUS_ATTEMPT_LIMIT = 6;

const parseStatusFailures = new Map<string, { lastErrorAt: number; count: number }>();

export function recordParseStatusFailure(
  id: string,
  state: { errorUpdatedAt: number; dataUpdatedAt: number; hasError: boolean }
): number {
  const previous = parseStatusFailures.get(id) ?? { lastErrorAt: 0, count: 0 };
  const next = nextParseStatusFailureCount(previous, state);
  parseStatusFailures.set(id, next);
  return next.count;
}

export function resetParseStatusFailures(id: string) {
  parseStatusFailures.delete(id);
}

export function getParseStatusFailureCount(id: string): number {
  return parseStatusFailures.get(id)?.count ?? 0;
}

export function useParseStatus(
  id: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: screenplayKeys.parseStatus(id!),
    queryFn: () => screenplayApi.getParseStatus(id!),
    enabled: !!id && (options?.enabled ?? true),
    // 永久请求错误无需重试；临时故障保留有限重试及后续轮询。
    retry: (failureCount, error) => !isNonRetryableHttpError(error) && failureCount < 3,
    refetchInterval: (query) => {
      if (!id || isNonRetryableHttpError(query.state.error)) return false;
      const attempts = recordParseStatusFailure(id, {
        errorUpdatedAt: query.state.errorUpdatedAt,
        dataUpdatedAt: query.state.dataUpdatedAt,
        hasError: Boolean(query.state.error),
      });
      if (attempts >= SCREENPLAY_PARSE_STATUS_ATTEMPT_LIMIT) return false;
      const data = query.state.data;
      if (!data && query.state.error) return options?.refetchInterval ?? 2000;
      if (data?.parsingStatus !== 'parsing' && data?.parsingStatus !== 'pending') return false;
      return data?.pollIntervalMs ?? options?.refetchInterval ?? 2000;
    },
    staleTime: 0, // 始终认为数据过期，确保轮询生效
    gcTime: 1000 * 60 * 5, // 5分钟
  });

  useEffect(() => {
    if (!id) return;

    const data = query.data;
    if (!data || data.parsingStatus !== 'completed') return;

    const parsedFileUrl = getParsedFileUrl(data);
    if (!data.content && !parsedFileUrl) return;

    queryClient.setQueryData(screenplayKeys.detail(id), (old: Screenplay | undefined) => {
      if (!old) return old;

      const nextWordCount = data.wordCount ?? old.wordCount;
      const nextParsingStatus = data.parsingStatus ?? old.parsingStatus;
      const shouldUpdateContent = !!data.content && old.content !== data.content;
      const shouldUpdateFileUrl = !!parsedFileUrl && old.fileUrl !== parsedFileUrl;
      const shouldUpdateWordCount = old.wordCount !== nextWordCount;
      const shouldUpdateParsingStatus = old.parsingStatus !== nextParsingStatus;

      if (
        !shouldUpdateContent &&
        !shouldUpdateFileUrl &&
        !shouldUpdateWordCount &&
        !shouldUpdateParsingStatus
      ) {
        return old;
      }

      return {
        ...old,
        ...(data.content ? { content: data.content } : {}),
        ...(parsedFileUrl ? { fileUrl: parsedFileUrl } : {}),
        wordCount: nextWordCount,
        parsingStatus: nextParsingStatus,
      };
    });
  }, [id, query.data, queryClient]);

  return query;
}

/**
 * 手动触发文件解析
 */
export function useParseScreenplayFile() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      screenplayApi.parseFile(id, force, activeOrgId),
    onSuccess: async (result, { id }) => {
      // 先清除上次失败，避免重试后轮询仍读到旧失败并立即停止。
      await queryClient.cancelQueries({ queryKey: screenplayKeys.parseStatus(id) });
      const parsingStatus = result.parsingStatus === 'completed' ? 'completed' : 'parsing';
      queryClient.setQueryData<ParseStatusResponse>(screenplayKeys.parseStatus(id), {
        screenplayId: id,
        parsingStatus,
        message: parsingStatus === 'completed' ? '解析完成' : '正在解析文件...',
      });
      queryClient.setQueryData<Screenplay>(screenplayKeys.detail(id), (previous) =>
        previous ? { ...previous, parsingStatus, parsingError: undefined } : previous
      );
      // 使解析状态缓存失效，触发重新查询
      queryClient.invalidateQueries({
        queryKey: screenplayKeys.parseStatus(id),
      });
    },
  });
}

export function useReorderScreenplays() {
  return useMutation({
    mutationFn: ({ projectId, orderedIds }: { projectId: string; orderedIds: string[] }) =>
      screenplayApi.reorder(projectId, orderedIds),
  });
}

/**
 * 获取剧本目录（合并所有分页），供 ParseFlow 工作台使用。
 * queryKey 与 useUpdateScreenplay / useImportStoryboard 的 dialogDirectoryRoot 前缀匹配，
 * 相关 mutation 的 invalidate/setQueriesData 会自动覆盖此缓存。
 */
export function useScreenplayDialogDirectory(
  projectId: string | null,
  options?: { refetchOnMount?: boolean | 'always' }
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: projectId
      ? screenplayKeys.dialogDirectory(projectId, activeOrgId)
      : [...screenplayKeys.dialogDirectoryRoot(), null, activeOrgId],
    queryFn: async () => {
      if (!projectId) return { items: [], total: 0, page: 1, pageSize: 100, totalPages: 1 };
      const pageSize = 100;
      const firstPage = await screenplayApi.getAll({ projectId, page: 1, pageSize });
      if (firstPage.totalPages <= 1) return firstPage;
      const remainingPages = await Promise.all(
        Array.from({ length: firstPage.totalPages - 1 }, (_, i) =>
          screenplayApi.getAll({ projectId, page: i + 2, pageSize })
        )
      );
      const allItems = [firstPage, ...remainingPages].flatMap((p) => p.items);
      return {
        ...firstPage,
        items: allItems,
        total: Math.max(firstPage.total, allItems.length),
        page: 1,
        pageSize: allItems.length || pageSize,
        totalPages: 1,
      };
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: options?.refetchOnMount,
  });
}

/**
 * 返回一个函数，用于批量获取项目的全部剧本（所有分页）。
 */
export function useFetchAllScreenplays() {
  return useCallback(async (projectId: string) => {
    const pageSize = 100;
    const firstPage = await screenplayApi.getAll({ projectId, page: 1, pageSize });
    if (firstPage.totalPages <= 1) return firstPage;
    const remainingPages = await Promise.all(
      Array.from({ length: firstPage.totalPages - 1 }, (_, i) =>
        screenplayApi.getAll({ projectId, page: i + 2, pageSize })
      )
    );
    const allItems = [firstPage, ...remainingPages].flatMap((p) => p.items);
    return {
      ...firstPage,
      items: allItems,
      total: Math.max(firstPage.total, allItems.length),
      page: 1,
      pageSize: allItems.length || pageSize,
      totalPages: 1,
    };
  }, []);
}

export function invalidateScreenplayListRoot(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: screenplayKeys.listRoot(projectId) });
}

export function invalidateScreenplayDialogDirectory(
  queryClient: QueryClient,
  projectId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: [...screenplayKeys.dialogDirectoryRoot(), projectId],
  });
}

export function removeScreenplayParseStatus(queryClient: QueryClient, screenplayId: string): void {
  queryClient.removeQueries({ queryKey: screenplayKeys.parseStatus(screenplayId) });
}

export function invalidateScreenplayScenes(queryClient: QueryClient, screenplayId: string): void {
  void queryClient.invalidateQueries({ queryKey: screenplayKeys.scenes(screenplayId) });
}
